import { CustomizableParser } from '../../CustomizableParser'
import {
  imGridStyling,
  latexConversion,
  removeManualSeparators,
} from './createNodeWrappers'
import { parseHtmlString } from 'ingestion/nodeCreationHelpers'
import { IMActivity, IMCourse, IMLessonAssessment } from '../types'
import {
  genericQuestionType,
  MultiAnswerProps,
  ShortAnswerProps,
} from '../../types'
import { wrapP } from '../../html_utility'

function gradeIdForCourse(course: IMCourse) {
  switch (true) {
    case course.slug.includes('grade-'):
      return parseInt(course.slug.split('grade-').pop()!) + 1
    case course.slug.includes('accelerated-'):
      return parseInt(course.slug.split('accelerated-').pop()!) + 1
    case course.slug.includes('kindergarten'):
      return 1
    case course.slug.includes('algebra-1'):
    case course.slug.includes('integrated-math-1'):
      return 10
    case course.slug.includes('geometry'):
    case course.slug.includes('integrated-math-2'):
      return 11
    case course.slug.includes('algebra-2'):
    case course.slug.includes('integrated-math-3'):
      return 12
    default:
      throw new Error(`Unrecognized Course: ${course.slug}`)
  }
}

function questionTitleEdit<T>(question: genericQuestionType<T>) {
  if (question.title?.toLowerCase() === 'are you ready for more?') {
    question.description = `${wrapP(question.title)}${question.description}`
  }
  return question
}
export class IM360Parser extends CustomizableParser {
  private static instance: IM360Parser
  private _gradeId: number

  constructor(gradeId: number) {
    super()
    this._gradeId = gradeId
  }

  public static getInstance(course?: IMCourse) {
    if (!IM360Parser.instance) {
      if (!course) {
        throw new Error('We really should start with a course')
      }
      const parser = new IM360Parser(gradeIdForCourse(course))
      parser.addContentDecorator(
        imGridStyling,
        removeManualSeparators,
        screenReaderOnly,
        latexConversion,
      )
      parser.addQuestionDecorator(questionTitleEdit)
      IM360Parser.instance = parser
    }
    if (course) {
      IM360Parser.instance._gradeId = gradeIdForCourse(course)
    }
    return IM360Parser.instance
  }

  get gradeId() {
    return this._gradeId
  }

  hasBooleanContext(htmlString: string): boolean {
    const asHtml = parseHtmlString(htmlString)
    return /^(true|false):/.test(asHtml.body.textContent?.toLowerCase() ?? '')
  }

  asMultipleChoiceAnswer(data: ShortAnswerProps) {
    // It is also important to use `this.` and not `super.`
    // This allows us the opportunity to use this classes' version
    // of the function first. And reserve `super.` only when we are
    // inside the overloaded method in question
    return this.createMultiAnswerQuestion(
      convertShortAnswerToTFMultiChoice(data),
    )
  }

  createShortAnswer(data: ShortAnswerProps) {
    const { teacherNote } = data
    if (teacherNote && this.hasBooleanContext(teacherNote)) {
      return this.asMultipleChoiceAnswer(data)
    }
    return super.createShortAnswer(data)
  }

  get misconceptionsTitle() {
    if (this.gradeId < 7) {
      return 'Advancing Student Thinking'
    }
    return 'Building On Student Thinking'
  }

  coolDownTitle(
    assessment: IMLessonAssessment,
    index: number,
    activities?: IMActivity[],
  ) {
    const title = `${assessment.type}: ${assessment.title}`.replace(
      'CoolDown',
      'Cool-down',
    )
    if (
      !activities ||
      !assessment.ancestors.lesson.ordinal_title ||
      this._gradeId < 7
    ) {
      return title
    }
    const lessonIndex = assessment.ancestors.lesson.ordinal_title
      .split(' ')
      .pop()
    return `${lessonIndex}.${index + activities.length + 1} ${title}`
  }
}

function convertShortAnswerToTFMultiChoice(
  data: ShortAnswerProps,
): MultiAnswerProps {
  const { teacherNote, description } = data
  if (!teacherNote) {
    throw new Error('Need teacher note')
  }
  const asText = parseHtmlString(teacherNote).body.textContent!.toLowerCase()
  const correctAnswer = normalizeBooleanAnswer(asText)
  return {
    answers: [
      { description: 'True', correct: correctAnswer },
      { description: 'False', correct: !correctAnswer },
    ],
    description,
    teacherNote,
  }
}

function normalizeBooleanAnswer(text: string) {
  if (text.startsWith('true')) {
    return true
  }

  if (text.startsWith('false')) {
    return false
  }

  throw new Error('bad assumption')
}

function screenReaderOnly(text: string) {
  const screenReaderClass = 'screenreader-only'
  if (!text.includes(screenReaderClass)) {
    return text
  }
  const asHtml = parseHtmlString(text)
  const elements = Array.from(asHtml.getElementsByClassName(screenReaderClass))
  elements.forEach((elm) => {
    let imgElm
    let previousSib: Element | null | undefined = elm
    do {
      previousSib = previousSib?.previousElementSibling
      if (previousSib?.tagName === 'IMG') {
        imgElm = previousSib
      }
    } while (!imgElm && previousSib)
    imgElm?.setAttribute('alt', elm.innerHTML)
    elm.remove()
  })
  return asHtml.body.innerHTML
}
